<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { createPdfFirstPagePreviewFromUrl } from 'dashboard/helper/pdfPreviewHelper';
import FileIcon from 'next/icon/FileIcon.vue';
import BaseBubble from './Base.vue';
import PdfConversationViewer from './PdfConversationViewer.vue';
import { useMessageContext } from '../provider.js';
import { ORIENTATION } from '../constants';

const { t } = useI18n();
const { attachments, content, orientation } = useMessageContext();

const isOutgoing = computed(() => orientation.value === ORIENTATION.RIGHT);

const attachment = computed(() => attachments.value?.[0] || {});
const url = computed(() => attachment.value.dataUrl || '');
const isPdfViewerOpen = ref(false);
const downloadedDocuments = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.DOWNLOADED_DOCUMENTS) || {}
);

const decodeFileName = value => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const fileName = computed(() => {
  const metadataName =
    attachment.value.fileName || attachment.value.file_name || '';
  if (metadataName) return decodeFileName(metadataName);

  const cleanUrl = String(url.value || '').split(/[?#]/, 1)[0];
  const name = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);
  return decodeFileName(name) || t('CONVERSATION.UNKNOWN_FILE_TYPE');
});

const fileExtension = computed(() => {
  const metadataExtension =
    attachment.value.extension || attachment.value.fileExtension || '';
  if (metadataExtension) return String(metadataExtension).replace('.', '');

  const match = fileName.value.match(/\.([a-z0-9]+)$/i);
  return match?.[1] || '';
});

const fileTypeLabel = computed(
  () => fileExtension.value.toUpperCase() || t('CONVERSATION.UNKNOWN_FILE_TYPE')
);

const isPdf = computed(() => {
  const contentType =
    attachment.value.contentType || attachment.value.content_type || '';
  return (
    contentType.toLowerCase() === 'application/pdf' ||
    fileExtension.value.toLowerCase() === 'pdf'
  );
});

const fileSizeLabel = computed(() => {
  const bytes = Number(
    attachment.value.fileSize ?? attachment.value.file_size ?? 0
  );
  if (!Number.isFinite(bytes) || bytes <= 0) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / 1024 ** unitIndex;
  const roundedValue =
    unitIndex === 0 || value >= 10 ? Math.round(value) : value.toFixed(1);

  return `${roundedValue} ${units[unitIndex]}`;
});

const caption = computed(() => {
  const value = String(content.value || '').trim();
  return ['mídia recebida', 'media received'].includes(value.toLowerCase())
    ? ''
    : value;
});

const openPdfViewer = () => {
  if (!isPdf.value || !url.value) return;
  isPdfViewerOpen.value = true;
};

const closePdfViewer = () => {
  isPdfViewerOpen.value = false;
};

const downloadedDocumentKey = computed(() =>
  String(attachment.value.id || url.value || '')
);

const isDownloaded = computed(
  () => !!downloadedDocuments.value[downloadedDocumentKey.value]
);

const markDocumentAsDownloaded = () => {
  const key = downloadedDocumentKey.value;
  if (!key) return;

  downloadedDocuments.value = {
    ...downloadedDocuments.value,
    [key]: true,
  };
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.DOWNLOADED_DOCUMENTS,
    downloadedDocuments.value
  );
};

const downloadDocument = () => {
  if (!url.value) return;

  markDocumentAsDownloaded();

  const link = document.createElement('a');
  link.href = url.value;
  link.download = fileName.value;
  link.rel = 'noreferrer noopener nofollow';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const handleFileClick = () => {
  if (isPdf.value) {
    openPdfViewer();
    return;
  }

  if (url.value) downloadDocument();
};

const previewImage = ref('');
const pdfPageCount = ref(0);
const isPreviewLoading = ref(false);

const pdfPageCountLabel = computed(() => {
  if (!isPdf.value || pdfPageCount.value <= 0) return '';
  return pdfPageCount.value === 1
    ? '1 página'
    : `${pdfPageCount.value} páginas`;
});

const generatePreview = async () => {
  if (!isPdf.value || !url.value || previewImage.value || isPreviewLoading.value) {
    return;
  }

  isPreviewLoading.value = true;
  try {
    const result = await createPdfFirstPagePreviewFromUrl(url.value);
    previewImage.value = result?.preview || '';
    pdfPageCount.value = Number(result?.pageCount) || 0;
  } catch {
    previewImage.value = '';
    pdfPageCount.value = 0;
  } finally {
    isPreviewLoading.value = false;
  }
};

onMounted(generatePreview);
watch(url, generatePreview);
</script>

<template>
  <BaseBubble
    class="acl-file-bubble overflow-hidden p-0 min-w-[320px]"
    :class="{ 'acl-file-bubble--download-only': !isPdf }"
    data-bubble-name="attachment"
    show-sender-name
    media-caption
  >
    <div
      v-if="isPdf && url"
      class="acl-file-bubble__preview cursor-pointer"
      role="button"
      tabindex="0"
      :aria-label="t('CONVERSATION.ATTACHMENT_VIEW')"
      @click="openPdfViewer"
      @keydown.enter.prevent="openPdfViewer"
      @keydown.space.prevent="openPdfViewer"
    >
      <img v-if="previewImage" :src="previewImage" alt="" />
    </div>

    <div
      class="acl-file-bubble__body"
      :class="{
        'cursor-pointer': isPdf || url,
        'acl-file-bubble__body--out': isOutgoing,
      }"
      :role="isPdf || url ? 'button' : undefined"
      :tabindex="isPdf || url ? 0 : undefined"
      :aria-label="!isPdf && url ? t('CONVERSATION.DOWNLOAD') : undefined"
      @click="handleFileClick"
      @keydown.enter.prevent="handleFileClick"
      @keydown.space.prevent="handleFileClick"
    >
      <div class="acl-file-bubble__file">
        <div class="acl-file-bubble__icon" aria-hidden="true">
          <FileIcon :file-type="fileExtension" />
        </div>
        <div class="acl-file-bubble__details">
          <div class="acl-file-bubble__name" :title="fileName">
            {{ fileName }}
          </div>
          <div class="acl-file-bubble__info">
            <span v-if="pdfPageCountLabel">{{ pdfPageCountLabel }}</span>
            <span
              v-if="pdfPageCountLabel"
              class="acl-file-bubble__separator"
              aria-hidden="true"
            />
            <span>{{ fileTypeLabel }}</span>
            <span
              v-if="fileSizeLabel"
              class="acl-file-bubble__separator"
              aria-hidden="true"
            />
            <span v-if="fileSizeLabel">{{ fileSizeLabel }}</span>
          </div>
        </div>

        <span
          v-if="!isPdf && url && !isDownloaded"
          v-tooltip="$t('CONVERSATION.DOWNLOAD')"
          class="acl-file-bubble__download grid size-12 shrink-0 place-content-center rounded-full bg-black/[0.06] text-n-slate-11 transition-colors hover:bg-black/10"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" height="34" width="34" fill="currentColor">
            <path
              fill="currentColor"
              d="M12 15.57a1.1 1.1 0 0 1-.38-.06.88.88 0 0 1-.32-.21l-3.6-3.6a.92.92 0 0 1-.29-.7 1.03 1.03 0 0 1 1-1.01c.28-.01.52.08.71.29L11 12.14V5c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71v7.15l1.88-1.88c.2-.2.43-.3.7-.28a1.02 1.02 0 0 1 1 1.01c.02.27-.08.5-.28.7l-3.6 3.6a.8.8 0 0 1-.32.21 1.1 1.1 0 0 1-.38.06ZM6 20c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41v-2c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71v2h12v-2c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71v2c0 .55-.2 1.02-.59 1.41-.39.4-.86.59-1.41.59H6Z"
            />
          </svg>
        </span>
      </div>

      <div v-if="caption" class="acl-file-bubble__caption">
        {{ caption }}
      </div>
    </div>

    <Teleport v-if="isPdfViewerOpen" to=".conversation-details-wrap">
      <PdfConversationViewer
        :url="url"
        :file-name="fileName"
        @close="closePdfViewer"
      />
    </Teleport>
  </BaseBubble>
</template>

<style scoped>
/* Aceleres: painel do documento no tom cinza/branco do WhatsApp — sempre
   contrasta com o verde/branco da bolha, independente do sentido da
   mensagem. O preview do PDF fica colado por cima, sem gap. */
.acl-file-bubble__preview {
  overflow: hidden;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.acl-file-bubble__preview img {
  display: block;
  width: 100%;
  height: auto;
}

.acl-file-bubble__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px;
  background: rgb(246, 245, 243);
}

/* Aceleres: fundo do painel (titulo + infos do arquivo) diferenciado por
   remetente, estilo WhatsApp — translucido sobre a bolha verde enviada. */
.acl-file-bubble__body.acl-file-bubble__body--out {
  background: rgba(194, 189, 184, 0.15);
}

.dark .acl-file-bubble__body {
  background: rgba(255, 255, 255, 0.06);
}

.acl-file-bubble__file {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.acl-file-bubble__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.acl-file-bubble__icon :deep(svg) {
  width: 40px;
  height: 40px;
}

.acl-file-bubble__details {
  min-width: 0;
  flex: 1;
}

.acl-file-bubble__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
}

.acl-file-bubble__info {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 1px;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.55);
}

.dark .acl-file-bubble__info {
  color: rgba(255, 255, 255, 0.6);
}

.acl-file-bubble__separator {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.7;
}

.acl-file-bubble__caption {
  margin-top: 8px;
  font-size: 14px;
  line-height: 19px;
}
</style>
